import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SlidersHorizontal, Trophy, CloudSun, Ticket, Bookmark, Sparkles, Crown, ArrowRight, Loader2 } from "lucide-react";
import { NewsCard } from "@/components/NewsCard";
import { getSaved, getSummary, onSavedChange } from "@/lib/membership";

const goTo = (id) => (e) => {
  const el = document.getElementById(id);
  if (el) { e.preventDefault(); el.scrollIntoView({ behavior: "smooth", block: "start" }); }
};

function Tile({ icon: Icon, label, value, href, anchor, testid }) {
  const cls = "group flex flex-col gap-1 rounded-2xl border border-hair bg-surfaceSecondary p-4 hover:border-brand/50 transition-colors";
  const body = (
    <>
      <Icon size={17} className="text-brand" />
      <span className="mt-1 font-serif text-xl font-semibold text-onSurface truncate">{value}</span>
      <span className="text-[11px] font-mono uppercase tracking-wider text-subtle">{label}</span>
    </>
  );
  return href
    ? <Link to={href} data-testid={testid} className={cls}>{body}</Link>
    : <a href={`#${anchor}`} onClick={goTo(anchor)} data-testid={testid} className={cls}>{body}</a>;
}

// "Il mio Readoo": saluto e riepilogo che porta alle sezioni già presenti nel profilo.
export function MyReadooHeader({ user }) {
  const [sum, setSum] = useState(null);
  useEffect(() => { getSummary().then(setSum).catch(() => {}); }, []);
  const name = user.first_name || (user.name || "").split(" ")[0] || "lettore";
  return (
    <div data-testid="my-readoo-header" className="mb-8">
      <p className="font-mono text-[11px] font-bold uppercase tracking-wider text-brand mb-2">Il mio Readoo</p>
      <h1 data-testid="my-readoo-hello" className="font-serif text-4xl sm:text-5xl font-semibold text-onSurface">Ciao {name}</h1>
      <p className="mt-1 text-base text-onSurfaceSecondary">Questo è il tuo Readoo.</p>
      {user.founder && (
        <span data-testid="my-readoo-founder-badge" className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand text-onBrand text-[11px] font-mono font-bold uppercase tracking-wider">
          <Crown size={12} /> Founder Readoo
        </span>
      )}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile icon={SlidersHorizontal} label="I tuoi interessi" value={`${(user.interests || []).length} selezionati`} anchor="interessi" testid="summary-interests" />
        <Tile icon={Trophy} label="Squadre seguite" value={(user.interest_tags || []).length} anchor="squadre" testid="summary-teams" />
        <Tile icon={CloudSun} label="Città meteo" value={user.meteo_city?.name || "Da scegliere"} anchor="meteo" testid="summary-city" />
        <Tile icon={Ticket} label="Vantaggi disponibili" value={sum ? sum.coupons_active : "…"} href="/coupon" testid="summary-coupons" />
      </div>
    </div>
  );
}

export function CreateReadooCard({ user }) {
  if ((user.interests || []).length >= 3) return null;
  return (
    <div data-testid="create-readoo-card" className="mb-6 rounded-2xl border border-brand/40 bg-brandTertiary/30 p-5">
      <h2 className="font-serif text-2xl font-semibold text-onSurface">Crea il tuo Readoo</h2>
      <p className="mt-1 text-sm text-onSurfaceSecondary">Scegli gli argomenti che ti interessano e Readoo costruisce per te un feed personale.</p>
      <a href="#interessi" onClick={goTo("interessi")} data-testid="create-readoo-cta"
        className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand text-onBrand text-sm font-bold uppercase tracking-wide hover:bg-brandSecondary transition-colors">
        Personalizza il tuo Readoo <ArrowRight size={15} />
      </a>
    </div>
  );
}

export function SavedSection() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    const load = () => getSaved().then((r) => setItems(r.articles || [])).catch(() => setItems([]));
    load();
    return onSavedChange(load);
  }, []);
  return (
    <div id="salvati" data-testid="saved-section" className="mt-6 rounded-2xl border border-hair bg-surfaceSecondary p-5 scroll-mt-28">
      <div className="flex items-center gap-2 mb-1">
        <Bookmark size={18} className="text-brand" />
        <h2 className="text-base font-semibold text-onSurface">Salvati per dopo</h2>
        {items && <span data-testid="saved-count" className="ml-auto font-mono text-xs text-subtle">{items.length}</span>}
      </div>
      {!items ? <Loader2 size={16} className="animate-spin text-subtle mt-3" />
        : items.length === 0 ? (
          <p data-testid="saved-empty" className="text-sm text-subtle mt-2">Ancora niente. Tocca «Salva» su un articolo per ritrovarlo qui quando vuoi.</p>
        ) : (
          <div data-testid="saved-list" className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {items.map((a) => <NewsCard key={a.article_id} article={a} />)}
          </div>
        )}
    </div>
  );
}

export function FounderCard({ user }) {
  if (!user.founder) return null;
  return (
    <div data-testid="founder-card" className="mt-6 relative overflow-hidden rounded-2xl bg-onSurface text-surface p-6">
      <span aria-hidden className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-brand/25" />
      <div className="relative">
        <span className="inline-flex items-center gap-1.5 text-brand font-mono text-[11px] font-bold uppercase tracking-wider"><Crown size={13} /> Status</span>
        <h2 className="mt-2 font-serif text-2xl font-semibold">Founder Readoo</h2>
        <p className="mt-2 text-sm leading-relaxed opacity-85">Sei entrato nella community durante la fase iniziale di Readoo. Sarai tra i primi a conoscere e provare le prossime novità.</p>
        <p className="mt-2 text-sm leading-relaxed opacity-85">Avrai inoltre accesso prioritario a Readoo Punti quando il programma sarà attivato.</p>
      </div>
    </div>
  );
}

export function PointsComingCard({ user }) {
  return (
    <div data-testid="points-coming-card" className="mt-6 rounded-2xl border border-hair bg-surfaceSecondary p-5">
      <div className="flex items-center gap-2">
        <Sparkles size={18} className="text-brand" />
        <h2 className="text-base font-semibold text-onSurface">Readoo Punti</h2>
        <span data-testid="points-coming-badge" className="ml-auto px-2.5 py-1 rounded-full bg-brand/15 text-brand font-mono text-[10px] font-bold uppercase tracking-wider">In arrivo</span>
      </div>
      <p className="mt-2 text-sm text-onSurfaceSecondary">Stiamo preparando un nuovo modo per premiare la tua partecipazione. Se sei già registrato, sarai tra i primi a poter accedere al programma.</p>
      {user.founder && <p data-testid="points-coming-founder" className="mt-2 text-sm font-semibold text-onSurface">Come Founder Readoo avrai accesso prioritario al lancio.</p>}
    </div>
  );
}
